<template>
  <div class="pos-left bg-white rounded-1">
    <div class="row mb-3">
      <div class="col-12 d-flex gap-2">
        <select 
          class="form-select w-auto" 
          v-model="selectedWarehouseId"
        >
          <option v-for="w in warehouses" :key="w.id" :value="w.id">
            {{ w.name }}
          </option>
        </select>
        <input
          type="text"
          class="form-control"
          :placeholder="$t('common.search_by_name_or_code')"
          v-model="searchQuery"
        />
      </div>
    </div>
    <div class="category-scroll-container mb-3">
      <ul
        class="nav nav-tabs pos-tablist d-flex flex-nowrap border-0 gap-2"
        role="tablist"
        style="overflow-x: auto; padding-bottom: 5px;"
      >
        <li class="nav-item">
          <button
            class="nav-link btn style-one rounded-3 d-flex flex-column align-items-center justify-content-center p-2 transition-all"
            :class="{ active: selectedCategoryId === null }"
            @click="selectedCategoryId = null"
            type="button"
            style="min-width: 90px; height: 90px;"
          >
            <div class="category-icon mb-2 rounded-circle d-flex align-items-center justify-content-center bg-light" style="width: 45px; height: 45px;">
              <img 
                :src="defaultCategoryImg" 
                :alt="$t('common.all')" 
                style="width: 25px; height: 25px; object-fit: contain;"
              />
            </div>
            <span class="fs-12 fw-medium text-dark text-truncate w-100 px-1">{{ $t("common.all") }}</span>
          </button>
        </li>
        <li class="nav-item" v-for="cat in categories" :key="cat.id">
          <button
            class="nav-link btn style-one rounded-3 d-flex flex-column align-items-center justify-content-center p-2 transition-all"
            :class="{ active: selectedCategoryId === cat.id }"
            @click="selectedCategoryId = cat.id!"
            type="button"
            style="min-width: 90px; height: 90px;"
          >
            <div class="category-icon mb-2 rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm" style="width: 45px; height: 45px; overflow: hidden;">
              <img 
                :src="getCategoryImage(cat)" 
                alt="icon" 
                style="width: 100%; height: 100%; object-fit: cover;"
              />
            </div>
            <span class="fs-12 fw-medium text-dark text-truncate w-100 px-1">{{ cat.name }}</span>
          </button>
        </li>
      </ul>
    </div>

    <div class="pos-grid mt-30">
      <div v-if="isLoading" class="text-center py-5 text-muted">
        {{ $t("pos.loading_products") }}
      </div>
      <div class="row gx-3 gy-3">
        <div 
          class="col-xl-3 col-lg-4 col-md-6 col-sm-6" 
          v-for="product in filteredProducts" 
          :key="product.id"
          @click="addToCart(product)"
        >
          <div class="product-item text-center border rounded p-0 overflow-hidden h-100 d-flex flex-column">
            <img
              :src="getProductImage(product)"
              alt="Image"
              class="d-block w-100 border-bottom"
              style="height: 140px; object-fit: contain; background: #f8f9fa;"
            />
            <div class="p-2 d-flex flex-column flex-grow-1">
            <span class="fs-12 text-optional d-block lh-1 mb-1">
              {{ product.category?.name || $t('pos.no_category') }}
            </span>
            <h6 class="fs-14 fw-semibold text-title mb-1 lh-1">
              {{ product.name }}
            </h6>
            <div class="d-flex justify-content-between align-items-center mb-1">
              <p class="fs-12 mb-0 text-primary fw-bold">{{ stateStore.formatPrice(product.price || 0) }}</p>
              <span v-if="product.type === 1" 
                :class="[
                  'badge fs-10 px-1 border',
                  getAvailableStock(product) > 0 ? 'bg-success-subtle text-success border-success-subtle' : 'bg-danger-subtle text-danger border-danger-subtle'
                ]"
              >
                {{ $t("pos.stock") }}: {{ getAvailableStock(product) }}
              </span>
            </div>
            <div class="d-flex justify-content-end align-items-center mt-auto">
              <div class="d-flex gap-1 flex-wrap justify-content-end">
                <span v-if="product.product_sizes?.length" class="badge bg-primary-subtle text-primary border border-primary-subtle fs-10 px-1">
                  {{ $t(product.product_sizes.length > 1 ? "pos.sizes" : "pos.size") }} :
                  {{ product.product_sizes.map(ps => ps.size?.name || '').filter(Boolean).join(', ') }}
                </span>
                <span v-if="product.sugar_id" class="badge bg-info-subtle text-info border border-info-subtle fs-10 px-1">{{ $t("pos.sugar") }}</span>
              </div>
            </div>
            </div>
          </div>
        </div>
        <div v-if="!isLoading && filteredProducts.length === 0" class="col-12 text-center py-5">
          <p class="text-muted">{{ $t("pos.no_products_found") }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import productService, { type Product } from "@/services/product";
import categoryService, { type Category } from "@/services/category";
import warehouseService, { type Warehouse } from "@/services/warehouse";
import stateStore from "@/utils/store";
import { buildAssetUrl, buildStorageAssetUrl } from "@/utils/settings";
import defaultCategoryImg from "@/assets/img/icons/category.svg";
import defaultProductImg from "@/assets/img/icons/products.svg";

export default defineComponent({
  name: "PosLeftContent",
  setup() {
    const products = ref<Product[]>([]);
    const categories = ref<Category[]>([]);
    const warehouses = ref<Warehouse[]>([]);
    const isLoading = ref(false);
    const selectedWarehouseId = ref<number | null>(null);
    const searchQuery = ref("");
    const debouncedSearchQuery = ref("");
    const selectedCategoryId = ref<number | null>(null);
    let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    const fetchProducts = async () => {
      isLoading.value = true;
      try {
        const params: any = {};
        if (selectedWarehouseId.value) {
          params.warehouse_id = selectedWarehouseId.value;
        }
        if (selectedCategoryId.value) {
          params.category_id = selectedCategoryId.value;
        }
        if (debouncedSearchQuery.value.trim()) {
          params.q = debouncedSearchQuery.value.trim();
        }

        const response = await productService.getProductsPage({
          ...params,
          pos: 1,
          per_page: 48,
        });
        products.value = response.data ?? [];
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        isLoading.value = false;
      }
    };

    const fetchCategories = async () => {
      try {
        categories.value = await categoryService.getCategories();
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    const filteredProducts = computed(() => products.value);

    const addToCart = (product: Product) => {
      stateStore.addSelectedProduct(product);
    };

    const getProductImage = (product: Product) => {
      if (product.attachments && product.attachments.length > 0) {
        const mainAtt = product.attachments.find(a => a.is_main === 1) || product.attachments[0];
        const filePath = mainAtt.file_path || (mainAtt as any).path;
        if (filePath) {
          return buildStorageAssetUrl(filePath);
        }
      }
      return defaultProductImg;
    };

    const getCategoryImage = (category: Category) => {
      if (typeof category.image === "string" && category.image) {
        return buildAssetUrl(category.image);
      }
      return defaultCategoryImg;
    };

    const getWarehouseStockTotal = (product: Product, warehouseId: number | null) => {
      if (!warehouseId) {
        return Number(product.qty) || 0;
      }

      const matchingStocks = (product.stocks || []).filter(
        (stock) => Number(stock.warehouse_id) === Number(warehouseId)
      );

      if (!matchingStocks.length) {
        return 0;
      }

      return matchingStocks.reduce((total, stock) => {
        const value = stock.qty ?? stock.quantity ?? 0;
        return total + (Number(value) || 0);
      }, 0);
    };

    const getAvailableStock = (product: Product) => {
      const warehouseId = selectedWarehouseId.value ?? stateStore.selectedWarehouseId;
      let stock = getWarehouseStockTotal(product, warehouseId);

      // Subtract quantity already in cart to show "Remaining Stock"
      const cartItem = stateStore.selectedProducts.find(p => p.id === product.id);
      if (cartItem) {
        stock -= (cartItem.qty || 0);
      }
      
      return stock;
    };

    watch(() => stateStore.refreshTrigger, () => {
      fetchProducts();
    });

    watch(selectedWarehouseId, (newId) => {
      if (newId) {
        const w = warehouses.value.find(x => x.id === newId);
        stateStore.setWarehouse(newId, w ? w.name : "");
      }
      fetchProducts();
    });

    watch(selectedCategoryId, () => {
      fetchProducts();
    });

    watch(searchQuery, (value) => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }

      searchDebounceTimer = setTimeout(() => {
        debouncedSearchQuery.value = value;
        fetchProducts();
      }, 250);
    });

    const fetchWarehouses = async () => {
      try {
        const res = await warehouseService.getWarehouses();
        if (Array.isArray(res)) {
          warehouses.value = res;
        } else if (res && (res as any).data) {
          warehouses.value = (res as any).data;
        }
        
        // Select first warehouse by default if list is not empty
        if (warehouses.value.length > 0 && selectedWarehouseId.value === null) {
          selectedWarehouseId.value = warehouses.value[0].id!;
        }
      } catch (err) {
        console.error("Failed to fetch warehouses:", err);
      }
    };

    onMounted(() => {
      fetchCategories();
      fetchWarehouses();
    });

    return {
      stateStore,
      products,
      categories,
      searchQuery,
      selectedCategoryId,
      filteredProducts,
      addToCart,
      getProductImage,
      getCategoryImage,
      getWarehouseStockTotal,
      getAvailableStock,
      defaultCategoryImg,
      warehouses,
      selectedWarehouseId,
      fetchProducts,
      isLoading,
    };
  },
});
</script>

<style lang="scss" scoped>
.pos-left {
  width: calc(60% - 10px);
  height: 100%;
  padding: 35px 15px 50px;
  overflow-y: auto;

  .pos-tablist {
    gap: 10px;
    .nav-item {
      flex: 0 0 auto;
      .nav-link {
        font-size: 13px;
        font-weight: 500;
        background-color: #ffffff !important;
        background: #ffffff !important;
        color: #333 !important;
        border: 1px solid #eee !important;
        transition: all 0.2s ease;
        border-radius: 12px !important;

        // Remove the global button hover effect (red background)
        &:before {
          display: none !important;
          content: none !important;
        }

        &:not(.active):hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0,0,0,0.05);
        }

        &.active {
          background-color: #fff !important;
          border: 2px solid var(--primaryColor, #4b7bec) !important;
          color: var(--primaryColor, #4b7bec) !important;
          box-shadow: none !important;
          transform: none !important;
          
          span {
            color: var(--primaryColor, #4b7bec) !important;
            font-weight: 700;
          }
          
          .category-icon {
             background-color: transparent !important;
             img {
               filter: none !important;
             }
          }
        }
      }
    }
  }
}

/* Hide scrollbar for Chrome, Safari and Opera */
.category-scroll-container ul::-webkit-scrollbar {
  height: 6px;
}

.category-scroll-container ul::-webkit-scrollbar-track {
  background: #f1f1f1; 
  border-radius: 4px;
}
 
.category-scroll-container ul::-webkit-scrollbar-thumb {
  background: #ccc; 
  border-radius: 4px;
}

.category-scroll-container ul::-webkit-scrollbar-thumb:hover {
  background: #bbb; 
}

.product-item {
  cursor: pointer;
  transition: all 0.2s;
  height: 100%;
  &:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    transform: translateY(-2px);
    border-color: var(--primaryColor, #4b7bec) !important;
  }
  img {
    max-width: 100%;
  }
}

@media only screen and (max-width: 575px) {
  .pos-left {
    padding: 15px 15px 30px;
  }
}

@media only screen and (max-width: 991px) {
  .pos-left {
    width: 100%;
    margin-bottom: 30px;
  }
}

@media only screen and (min-width: 1600px) {
  .pos-left {
    width: calc(65% - 10px);
  }
}
</style>
